import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { CALENDAR_Z_INDICES } from '../constants';
import { useCalendarGridStore } from '../store/calendar-grid.store';
import { EmptyStateRenderProp } from '../types';

interface Props {
  renderEmptyStateComponent: EmptyStateRenderProp;
}

/**
 * Renders per-group empty state overlays when groups have no events or no columns.
 * Handles visibility logic and gridColumn calculation internally.
 */
export const EmptyStateOverlays = ({ renderEmptyStateComponent }: Props) => {
  const { groupIds, groupsById } = useCalendarGridStore((state) => ({
    groupIds: state.groupIds,
    groupsById: state.groupsById,
  }));

  /**
   * Calculates the grid column range for a specific group
   * Grid structure: [time-column] [group1-cols + separator] [group2-cols + separator] ...
   */
  const getGroupGridColumnRange = useCallback(
    (groupIndex: number): string => {
      // Start column: 2 (after time column which is column 1)
      // Each group takes: columns + 1 separator
      let startCol = 2;

      for (let i = 0; i < groupIndex; i++) {
        const groupId = groupIds[i];
        let colCount = groupsById[groupId]?.columnIds.length ?? 0;
        // Force minimum 1 column for groups with 0 columns
        if (colCount === 0) colCount = 1;
        startCol += colCount + 1; // +1 for separator
      }

      const currentGroupId = groupIds[groupIndex];
      let currentColCount = groupsById[currentGroupId]?.columnIds.length ?? 0;
      // Force minimum 1 column for groups with 0 columns
      if (currentColCount === 0) currentColCount = 1;

      // End column is inclusive: start + columns
      const endCol = startCol + currentColCount;

      return `${startCol} / ${endCol}`;
    },
    [groupIds, groupsById]
  );

  const isCombinedNoColumnsState =
    groupIds.length > 0 && groupIds.every((groupId) => groupsById[groupId]?.emptyStateVariant === 'no-columns');

  // Memoize to avoid unnecessary re-renders
  const overlayElements = useMemo(() => {
    if (isCombinedNoColumnsState) {
      const content = renderEmptyStateComponent(groupIds, 'no-columns');
      if (content === null) return null;

      const lastGroupRange = getGroupGridColumnRange(groupIds.length - 1);
      const endColumn = lastGroupRange.split(' / ')[1];

      return (
        <div key='empty-state-all-groups' css={[emptyStateOverlayStyle, { gridColumn: `2 / ${endColumn}` }]}>
          <div css={emptyStateContentStyle}>{content}</div>
        </div>
      );
    }

    return groupIds.map((groupId, groupIndex) => {
      const groupInfo = groupsById[groupId];
      if (!groupInfo) return null;

      const variant = groupInfo.emptyStateVariant;
      if (!variant) return null;

      const gridColumnRange = getGroupGridColumnRange(groupIndex);
      const content = renderEmptyStateComponent([groupId], variant);

      if (content === null) return null;

      return (
        <div key={`empty-state-${groupId}`} css={[emptyStateOverlayStyle, { gridColumn: gridColumnRange }]}>
          <div css={emptyStateContentStyle}>{content}</div>
        </div>
      );
    });
  }, [getGroupGridColumnRange, groupIds, groupsById, isCombinedNoColumnsState, renderEmptyStateComponent]);

  return <>{overlayElements}</>;
};

const emptyStateOverlayStyle = css({
  width: '100%',
  backdropFilter: 'blur(2px)',
  zIndex: CALENDAR_Z_INDICES.timeColumn - 1,
  height: '100%',
  gridRow: '4 / -1',
  justifySelf: 'start',
  alignSelf: 'start',
});

const emptyStateContentStyle = css({
  position: 'sticky',
  top: 0,
  width: '100%',
  height: '100%',
  maxHeight: 'calc(var(--viewport-height) - 85px)',
});
